import { useEffect, useId, useMemo, useState } from "react";

/**
 * Foto do contato — a mesma linha na criação e na edição.
 *
 * A foto pertence às três superfícies do cadastro (§7.1, §25.4). O componente
 * só escolhe e valida o arquivo: quem grava é quem tem o id do lead, porque na
 * criação ele ainda não existe no momento em que a foto é escolhida.
 */

const PHOTO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const PHOTO_MAX_BYTES = 2 * 1024 * 1024;

export interface LeadPhotoFieldProps {
  /** Foto já gravada, mostrada enquanto nenhuma nova for escolhida. */
  currentUrl?: string;
  file: File | null;
  onChange: (file: File | null) => void;
}

export function LeadPhotoField({ currentUrl, file, onChange }: LeadPhotoFieldProps) {
  const inputId = useId();
  const [error, setError] = useState<string | null>(null);
  // A prévia é enfeite: sem `createObjectURL` o campo continua funcionando, e
  // a foto escolhida continua sendo enviada.
  const preview = useMemo(() => {
    if (!file || typeof URL.createObjectURL !== "function") return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => () => {
    if (preview && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(preview);
  }, [preview]);

  const escolhe = (chosen: File | null) => {
    if (!chosen) {
      setError(null);
      onChange(null);
      return;
    }
    // O tipo declarado é conferido aqui para o erro aparecer antes do envio; o
    // comando confere de novo, e é ele quem de fato recusa (§9).
    if (!PHOTO_TYPES.includes(chosen.type)) {
      setError("Use PNG, JPG ou WebP.");
      onChange(null);
      return;
    }
    if (chosen.size > PHOTO_MAX_BYTES) {
      setError("A foto precisa ter até 2 MB.");
      onChange(null);
      return;
    }
    setError(null);
    onChange(chosen);
  };

  const mostrada = preview ?? currentUrl;

  return (
    <div className="z-photo-row">
      {mostrada && <img src={mostrada} alt="" />}
      <div>
        <label htmlFor={inputId}>Foto do contato</label>
        <input
          id={inputId}
          type="file"
          accept={PHOTO_TYPES.join(",")}
          aria-describedby={`${inputId}-help`}
          aria-invalid={error ? true : undefined}
          onChange={(event) => escolhe(event.target.files?.[0] ?? null)}
        />
        <small id={`${inputId}-help`} className="z-help">
          {error ?? "PNG, JPG ou WebP · até 2 MB"}
        </small>
      </div>
    </div>
  );
}
